import type { MainToWorkerMessage, PedagogyUiGroup, PedagogyUiState } from '../shell/protocol';
import {
  getStoredPanelFontSize,
  getStoredPanelHeight,
  getStoredPanelWidth,
  storePanelFontSize,
  storePanelHeight,
  storePanelWidth,
} from './theme';
import { t } from './i18n';

export interface PedagogyPanelOptions {
  send(message: MainToWorkerMessage): void;
  /** Appelé quand le panneau change de visibilité (pour recalculer le terminal). */
  onToggle?(): void;
}

export interface PedagogyPanel {
  update(state: PedagogyUiState): void;
  open(): void;
}

type TabName = 'active' | 'tutorials' | 'challenges';

const DEFAULT_PANEL_FONT = 13;
const MIN_PANEL_FONT = 10;
const MAX_PANEL_FONT = 22;
const MIN_WIDTH = 240;
const DEFAULT_WIDTH = 400;

function byId<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Élément #${id} introuvable`);
  return element as T;
}

/** Panneau latéral : navigation par onglets, exercice en cours et journal. */
export function setupPedagogyPanel(options: PedagogyPanelOptions): PedagogyPanel {
  const panel = byId<HTMLElement>('pedagogy-panel');
  const toggleButton = byId<HTMLButtonElement>('btn-pedagogy');
  const closeButton = byId<HTMLButtonElement>('btn-pedagogy-close');
  const level = byId<HTMLElement>('pedagogy-level');
  const xp = byId<HTMLElement>('pedagogy-xp');
  const badges = byId<HTMLElement>('pedagogy-badges');
  const tracksContainer = byId<HTMLElement>('pedagogy-tracks');
  const levelsContainer = byId<HTMLElement>('pedagogy-levels');
  const resizeHandle = byId<HTMLElement>('pedagogy-resize');
  const empty = byId<HTMLElement>('pedagogy-empty');
  const active = byId<HTMLElement>('pedagogy-active');
  const kindChip = byId<HTMLElement>('pedagogy-active-kind');
  const trackLabel = byId<HTMLElement>('pedagogy-active-track');
  const title = byId<HTMLElement>('pedagogy-active-title');
  const progressLabel = byId<HTMLElement>('pedagogy-progress');
  const bar = byId<HTMLProgressElement>('pedagogy-bar');
  const instruction = byId<HTMLElement>('pedagogy-instruction');
  const hintsContainer = byId<HTMLElement>('pedagogy-hints');
  const feedbackList = byId<HTMLElement>('pedagogy-feedback');
  const skip = byId<HTMLButtonElement>('pedagogy-skip');
  const stop = byId<HTMLButtonElement>('pedagogy-stop');
  const flagForm = byId<HTMLFormElement>('pedagogy-flag-form');
  const flagInput = byId<HTMLInputElement>('pedagogy-flag-input');

  let current: PedagogyUiState | null = null;

  // --- En-tête compact : le titre se réduit à l'icône si le panneau est étroit.
  const narrowObserver = new ResizeObserver(() => {
    panel.classList.toggle('is-narrow', panel.clientWidth < 350);
  });
  narrowObserver.observe(panel);

  // --- Onglets Material
  const tabs: Record<TabName, HTMLButtonElement> = {
    active: byId<HTMLButtonElement>('tab-pedagogy-active'),
    tutorials: byId<HTMLButtonElement>('tab-pedagogy-tutorials'),
    challenges: byId<HTMLButtonElement>('tab-pedagogy-challenges'),
  };
  const panes: Record<TabName, HTMLElement> = {
    active: byId<HTMLElement>('pane-pedagogy-active'),
    tutorials: byId<HTMLElement>('pane-pedagogy-tutorials'),
    challenges: byId<HTMLElement>('pane-pedagogy-challenges'),
  };
  const selectTab = (name: TabName): void => {
    for (const key of Object.keys(tabs) as TabName[]) {
      const selected = key === name;
      tabs[key].setAttribute('aria-selected', String(selected));
      tabs[key].classList.toggle('is-selected', selected);
      panes[key].hidden = !selected;
    }
    options.onToggle?.();
  };
  for (const key of Object.keys(tabs) as TabName[]) {
    tabs[key].addEventListener('click', () => selectTab(key));
  }
  byId<HTMLButtonElement>('pedagogy-browse-tutorials').addEventListener('click', () =>
    selectTab('tutorials'),
  );
  byId<HTMLButtonElement>('pedagogy-browse-challenges').addEventListener('click', () =>
    selectTab('challenges'),
  );

  // --- Taille de police propre au panneau (indépendante de la console)
  let panelFont = getStoredPanelFontSize() ?? DEFAULT_PANEL_FONT;
  const applyPanelFont = (size: number): void => {
    panelFont = Math.max(MIN_PANEL_FONT, Math.min(MAX_PANEL_FONT, size));
    panel.style.fontSize = `${panelFont}px`;
    storePanelFontSize(panelFont);
  };
  applyPanelFont(panelFont);
  byId<HTMLButtonElement>('btn-panel-font-dec').addEventListener('click', () =>
    applyPanelFont(panelFont - 1),
  );
  byId<HTMLButtonElement>('btn-panel-font-inc').addEventListener('click', () =>
    applyPanelFont(panelFont + 1),
  );

  // --- Redimensionnement : largeur (bureau) ou hauteur (tablette empilée)
  const clampWidth = (width: number): number =>
    Math.max(MIN_WIDTH, Math.min(Math.min(900, window.innerWidth - 200), width));
  const applyWidth = (width: number): void => {
    panel.style.width = `${clampWidth(width)}px`;
  };
  const clampHeight = (height: number): number =>
    Math.max(140, Math.min(Math.round(window.innerHeight * 0.8), height));
  const setPanelHeight = (height: number): void => {
    panel.style.height = `${clampHeight(height)}px`;
  };
  const stacked = (): boolean => window.innerWidth <= 820;
  if (stacked()) {
    const stored = getStoredPanelHeight();
    if (stored !== null) setPanelHeight(stored);
  } else {
    applyWidth(getStoredPanelWidth() ?? DEFAULT_WIDTH);
  }

  resizeHandle.addEventListener('pointerdown', (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    resizeHandle.classList.add('dragging');
    resizeHandle.setPointerCapture(event.pointerId);
    const horizontal = stacked();
    const startX = event.clientX;
    const startY = event.clientY;
    const rect = panel.getBoundingClientRect();
    const onMove = (move: PointerEvent): void => {
      if (horizontal) setPanelHeight(rect.height + (startY - move.clientY));
      else applyWidth(rect.width + (startX - move.clientX));
      options.onToggle?.();
    };
    const onUp = (): void => {
      resizeHandle.classList.remove('dragging');
      resizeHandle.removeEventListener('pointermove', onMove);
      resizeHandle.removeEventListener('pointerup', onUp);
      resizeHandle.removeEventListener('pointercancel', onUp);
      if (horizontal) storePanelHeight(panel.getBoundingClientRect().height);
      else if (!panel.hidden) storePanelWidth(panel.getBoundingClientRect().width);
    };
    resizeHandle.addEventListener('pointermove', onMove);
    resizeHandle.addEventListener('pointerup', onUp);
    resizeHandle.addEventListener('pointercancel', onUp);
  });
  window.addEventListener('resize', () => {
    if (panel.hidden) return;
    if (stacked()) {
      setPanelHeight(getStoredPanelHeight() ?? panel.getBoundingClientRect().height);
    } else {
      panel.style.height = '';
      applyWidth(getStoredPanelWidth() ?? panel.getBoundingClientRect().width);
    }
  });

  // --- Catalogue : parcours/niveaux dépliables, lancement en un clic
  const openGroups = new Set<string>();
  const renderGroup = (
    container: HTMLElement,
    kind: 'tutorial' | 'challenge',
    rows: ReadonlyArray<PedagogyUiGroup>,
  ): void => {
    container.replaceChildren();
    for (const row of rows) {
      const key = `${kind}:${row.name}`;
      const group = document.createElement('details');
      group.className = 'pedagogy-group';
      group.open = openGroups.has(key) || row.items.some((item) => item.active);
      group.addEventListener('toggle', () => {
        if (group.open) openGroups.add(key);
        else openGroups.delete(key);
      });

      const summary = document.createElement('summary');
      const name = document.createElement('span');
      name.className = 'pedagogy-group-name';
      name.textContent = row.name;
      const value = document.createElement('span');
      value.className = 'pedagogy-row-value';
      value.textContent = `${row.done}/${row.total}`;
      summary.append(name, value);
      group.append(summary);

      for (const item of row.items) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pedagogy-item';
        if (item.done) button.classList.add('is-done');
        if (item.active) button.classList.add('is-active');
        const marker = document.createElement('span');
        marker.className = 'pedagogy-item-marker';
        marker.textContent = item.active ? '▶' : item.done ? '✓' : '○';
        const label = document.createElement('span');
        label.className = 'pedagogy-item-label';
        label.textContent = item.title;
        button.append(marker, label);
        button.title = t(kind === 'tutorial' ? 'panel.startTutorial' : 'panel.startChallenge');
        button.addEventListener('click', () => {
          if (!item.active) {
            options.send({ type: 'input', data: `${kind} start ${item.id}\r` });
          }
          selectTab('active');
        });
        group.append(button);
      }
      container.append(group);
    }
  };

  // --- Exercice en cours
  const renderHints = (state: PedagogyUiState): void => {
    hintsContainer.replaceChildren();
    const hints = state.activeTutorial?.hints ?? state.activeChallenge?.hints ?? [];
    for (const hint of hints) {
      const line = document.createElement('p');
      line.className = 'pedagogy-hint-line';
      line.textContent = hint;
      hintsContainer.append(line);
    }
  };

  const renderActive = (state: PedagogyUiState): void => {
    const hasActive = state.activeTutorial !== null || state.activeChallenge !== null;
    empty.hidden = hasActive;
    active.hidden = !hasActive;
    hintsContainer.replaceChildren();
    if (state.activeTutorial) {
      const step = state.activeTutorial;
      kindChip.textContent = t('panel.tutorialBadge');
      trackLabel.textContent = step.track;
      title.textContent = step.title;
      progressLabel.textContent = `${t('panel.step')} ${step.step}/${step.total}`;
      bar.max = step.total;
      bar.value = step.step - 1;
      instruction.textContent = step.instruction;
      skip.hidden = false;
      stop.hidden = false;
      flagForm.hidden = true;
    } else if (state.activeChallenge) {
      const challenge = state.activeChallenge;
      kindChip.textContent = t('panel.challengeBadge');
      trackLabel.textContent = `${challenge.level} · ${t('panel.difficulty')} ${challenge.difficulty}/5`;
      title.textContent = challenge.title;
      progressLabel.textContent = t('panel.challengeGoal');
      bar.max = 1;
      bar.value = 0;
      instruction.textContent = challenge.description;
      skip.hidden = true;
      stop.hidden = false;
      flagForm.hidden = false;
    }
    renderHints(state);
  };

  // --- Journal des validations
  const formatTime = (at: number): string =>
    new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const renderFeedback = (state: PedagogyUiState): void => {
    feedbackList.replaceChildren();
    if (state.feedback.length === 0) {
      const emptyItem = document.createElement('li');
      emptyItem.className = 'pedagogy-muted';
      emptyItem.textContent = t('panel.journalEmpty');
      feedbackList.append(emptyItem);
      return;
    }
    for (const entry of state.feedback) {
      const item = document.createElement('li');
      item.className = `pedagogy-feedback-item is-${entry.kind}`;
      const time = document.createElement('span');
      time.className = 'pedagogy-feedback-time';
      time.textContent = formatTime(entry.at);
      const text = document.createElement('span');
      text.textContent = entry.text;
      item.append(time, text);
      feedbackList.append(item);
    }
  };

  // --- Actions
  byId<HTMLButtonElement>('pedagogy-hint').addEventListener('click', () => {
    options.send({ type: 'input', data: 'hint\r' });
  });
  skip.addEventListener('click', () => {
    options.send({ type: 'input', data: 'tutorial skip\r' });
  });
  stop.addEventListener('click', () => {
    const command = current?.activeTutorial ? 'tutorial stop' : 'challenge stop';
    options.send({ type: 'input', data: `${command}\r` });
  });
  flagForm.addEventListener('submit', (event) => {
    event.preventDefault();
    const flag = flagInput.value.trim();
    if (flag === '') return;
    options.send({ type: 'input', data: `challenge submit ${flag}\r` });
    flagInput.value = '';
  });

  const setOpen = (open: boolean): void => {
    panel.hidden = !open;
    resizeHandle.hidden = !open;
    toggleButton.setAttribute('aria-expanded', String(open));
    options.onToggle?.();
  };
  toggleButton.addEventListener('click', () => setOpen(panel.hidden === true));
  closeButton.addEventListener('click', () => setOpen(false));

  let hadActive = false;
  return {
    open: () => setOpen(true),
    update(state) {
      current = state;
      level.textContent = `${t('panel.level')} ${state.level} — ${state.levelTitle}`;
      xp.textContent = `${state.xp} XP`;
      badges.textContent = `${state.badges} ${state.badges > 1 ? t('panel.badges') : t('panel.badge')}`;
      renderGroup(tracksContainer, 'tutorial', state.tracks);
      renderGroup(levelsContainer, 'challenge', state.levels);
      renderActive(state);
      renderFeedback(state);

      const hasActive = state.activeTutorial !== null || state.activeChallenge !== null;
      if (hasActive && !hadActive) {
        setOpen(true);
        selectTab('active');
      }
      hadActive = hasActive;
    },
  };
}
